import React, { useState, useMemo, useCallback } from 'react';
import {
  tokens,
} from '../design-system';
import { ArrowUturnLeftIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useIdeaSnapshots, useRollbackSnapshot } from '../hooks/api/useIdeaSnapshots';
import { useIdeaEditHistory, useUndoIdeaEdit } from '../hooks/api/useIdeaEdit';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

const SECTION_LABELS = {
  executive_summary: 'Executive Summary',
  problem_statement: 'Problem Statement',
  target_users: 'Target Users',
  value_proposition: 'Value Proposition',
  success_metrics: 'Success Metrics',
  risks_and_mitigations: 'Risks & Mitigations',
  core_features: 'Features',
  discovery_answers: 'Assumptions',
};

const TRIGGER_ICONS = {
  spec_edit: '✏️',
  drift_action: '🔄',
  chat_proposal: '💬',
  pre_rollback: '⏪',
};

function formatTime(isoString) {
  const d = new Date(isoString);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

/**
 * HistorySidebar - Slide-out panel showing idea change history.
 *
 * Merges edit history (step-by-step changes) and snapshots (full state captures)
 * into a single timeline. Supports undo (latest edit) and rollback (any snapshot).
 */
const HistorySidebar = ({ ideaId, isOpen, onClose, onRefetch }) => {
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [expandedEdit, setExpandedEdit] = useState(null);

  const { data: snapshots, isLoading: snapshotsLoading } = useIdeaSnapshots(ideaId);
  const { data: editHistoryData, isLoading: editsLoading } = useIdeaEditHistory(ideaId);
  const rollbackMutation = useRollbackSnapshot();
  const undoMutation = useUndoIdeaEdit();

  const snapshotList = Array.isArray(snapshots) ? snapshots : [];
  const editList = editHistoryData?.edits || [];

  // Merge into unified timeline sorted by time descending
  const timeline = useMemo(() => {
    const items = [];

    editList.forEach((edit) => {
      items.push({
        type: 'edit',
        time: edit.created_at,
        data: edit,
      });
    });

    snapshotList.forEach((snap) => {
      items.push({
        type: 'snapshot',
        time: snap.created_at,
        data: snap,
      });
    });

    items.sort((a, b) => new Date(b.time) - new Date(a.time));
    return items;
  }, [editList, snapshotList]);

  const handleUndo = useCallback(async () => {
    if (undoMutation.isPending) return; // Prevent double-click
    try {
      await undoMutation.mutateAsync({ ideaId });
      if (onRefetch) onRefetch();
    } catch (err) {
      console.error('Undo failed:', err);
    }
  }, [ideaId, undoMutation, onRefetch]);

  const handleRollback = useCallback(async () => {
    if (!rollbackTarget) return;
    try {
      await rollbackMutation.mutateAsync({
        ideaId,
        snapshotId: rollbackTarget.id,
      });
      setRollbackTarget(null);
      if (onRefetch) onRefetch();
    } catch (err) {
      console.error('Rollback failed:', err);
    }
  }, [ideaId, rollbackTarget, rollbackMutation, onRefetch]);

  if (!isOpen) return null;

  const isLoading = snapshotsLoading || editsLoading;

  return (
    <>
      {/* Panel */}
      <div style={{
        position: 'fixed',
        top: 60,
        right: 0,
        width: '248px',
        height: 'calc(100vh - 60px)',
        backgroundColor: T.paperDeep,
        borderLeft: `1px dashed ${T.rule}`,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(46,33,9,0.10)',
        fontFamily: T.sans,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${T.rule}`,
          backgroundColor: T.paperSoft,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ClockIcon style={{ width: 16, height: 16, color: T.orange }} />
            <span style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 700,
              color: T.ink,
              fontFamily: T.sans,
              letterSpacing: '-0.01em',
            }}>
              History
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: T.inkFaint,
              padding: '4px',
              lineHeight: 1,
              fontFamily: T.sans,
            }}
          >
            &times;
          </button>
        </div>

        {/* Timeline */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <span style={{ fontSize: '12px', color: T.inkFaint, fontFamily: T.sans }}>
                Loading history...
              </span>
            </div>
          ) : timeline.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <span style={{ fontSize: '12px', color: T.inkFaint, fontFamily: T.sans }}>
                No changes yet
              </span>
            </div>
          ) : (
            <div style={{ padding: '8px 0' }}>
              {timeline.map((item) => {
                const itemKey = item.type === 'edit'
                  ? `edit-${item.data.version_number}-${item.time}`
                  : `snap-${item.data.id}`;
                return (
                  <div key={itemKey}>
                    {item.type === 'edit' ? (
                      <EditEntry
                        edit={item.data}
                        isExpanded={expandedEdit === itemKey}
                        onToggle={() => setExpandedEdit(expandedEdit === itemKey ? null : itemKey)}
                        onUndo={item.data.can_undo ? handleUndo : null}
                        undoPending={undoMutation.isPending}
                      />
                    ) : (
                      <SnapshotEntry
                        snapshot={item.data}
                        onRollback={() => setRollbackTarget(item.data)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Rollback Confirmation Overlay */}
      {rollbackTarget && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(46,33,9,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            backgroundColor: T.card,
            borderRadius: T.r4,
            boxShadow: T.sh2,
            padding: '24px',
            maxWidth: 380,
            width: '90%',
            fontFamily: T.sans,
            border: `1px solid ${T.rule}`,
          }}>
            <h3 style={{
              margin: '0 0 12px',
              fontSize: '16px',
              fontWeight: 700,
              color: T.ink,
              fontFamily: T.sans,
            }}>
              Rollback to Snapshot?
            </h3>
            <p style={{
              margin: '0 0 8px',
              fontSize: '13px',
              color: T.inkMuted,
              lineHeight: 1.5,
              fontFamily: T.sans,
            }}>
              Restore to <strong style={{ color: T.ink2 }}>v{rollbackTarget.idea_spec_version}</strong>{' '}
              ({rollbackTarget.trigger_description}).
            </p>
            <p style={{
              margin: '0 0 8px',
              fontSize: '12px',
              color: T.inkFaint,
              lineHeight: 1.5,
              fontFamily: T.sans,
            }}>
              All spec changes, features, and stories will revert. Plans created after this snapshot will be deleted.
            </p>
            <p style={{
              margin: '0 0 20px',
              fontSize: '12px',
              color: T.moss,
              lineHeight: 1.5,
              fontFamily: T.sans,
            }}>
              A safety snapshot will be taken first so you can undo this rollback.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRollbackTarget(null)}
                disabled={rollbackMutation.isPending}
                style={{
                  padding: '7px 16px',
                  backgroundColor: 'transparent',
                  border: `1px dashed ${T.rule}`,
                  borderRadius: T.r3,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: T.inkMuted,
                  fontFamily: T.sans,
                  opacity: rollbackMutation.isPending ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRollback}
                disabled={rollbackMutation.isPending}
                style={{
                  padding: '7px 16px',
                  backgroundColor: rollbackMutation.isPending ? T.paperDeep : T.orange,
                  border: 'none',
                  borderRadius: T.r3,
                  cursor: rollbackMutation.isPending ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: rollbackMutation.isPending ? T.inkFaint : '#fff',
                  fontFamily: T.sans,
                  boxShadow: rollbackMutation.isPending ? 'none' : T.sh1,
                }}
              >
                {rollbackMutation.isPending ? 'Rolling back...' : 'Rollback'}
              </button>
            </div>
            {rollbackMutation.isError && (
              <p style={{
                margin: '12px 0 0',
                fontSize: '12px',
                color: T.capRed,
                fontFamily: T.sans,
              }}>
                {rollbackMutation.error?.message || 'Rollback failed'}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/** Single edit history entry */
const EditEntry = ({ edit, isExpanded, onToggle, onUndo, undoPending }) => {
  const sectionLabel = SECTION_LABELS[edit.section] || edit.section;
  const isDriftEdit = edit.created_by && edit.created_by.startsWith('system:drift');

  return (
    <div style={{
      margin: '4px 10px',
      backgroundColor: T.card,
      borderRadius: T.r2,
      boxShadow: T.sh1,
      borderLeft: `3px solid ${T.rule}`,
      overflow: 'hidden',
    }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          padding: '9px 10px',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <div style={{ fontSize: '13px', flexShrink: 0, marginTop: '1px' }}>
          {isDriftEdit ? '\uD83D\uDD04' : edit.is_undo ? '\u21A9\uFE0F' : '\u270F\uFE0F'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: T.ink,
            fontFamily: T.sans,
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {isDriftEdit && (
              <span style={{
                display: 'inline-block',
                padding: '1px 5px',
                marginRight: '5px',
                fontSize: '10px',
                fontWeight: 700,
                borderRadius: T.r2,
                backgroundColor: `${T.moss}20`,
                color: T.moss,
                fontFamily: T.sans,
              }}>
                DRIFT
              </span>
            )}
            {edit.is_undo ? 'Undo: ' : ''}{sectionLabel}
          </div>
          <div style={{
            fontSize: '11px',
            color: T.inkFaint,
            fontFamily: T.mono,
            marginTop: '2px',
          }}>
            v{edit.version_number}
            {edit.cascade_count > 0 && ` · ${edit.cascade_count} cascades`}
            {' · '}{formatTime(edit.created_at)}
          </div>
        </div>
        {onUndo && (
          <button
            onClick={(e) => { e.stopPropagation(); onUndo(); }}
            disabled={undoPending}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              padding: '2px 7px',
              fontSize: '11px',
              color: T.inkMuted,
              backgroundColor: 'transparent',
              border: `1px dashed ${T.rule}`,
              borderRadius: T.full,
              cursor: undoPending ? 'not-allowed' : 'pointer',
              fontFamily: T.sans,
              opacity: undoPending ? 0.5 : 1,
            }}
            title="Undo this edit"
          >
            <ArrowUturnLeftIcon style={{ width: 10, height: 10 }} />
            {undoPending ? '...' : 'Undo'}
          </button>
        )}
      </div>
    </div>
  );
};

/** Single snapshot entry */
const SnapshotEntry = ({ snapshot, onRollback }) => {
  return (
    <div style={{
      margin: '4px 10px',
      backgroundColor: T.card,
      borderRadius: T.r2,
      boxShadow: T.sh1,
      borderLeft: `3px solid ${T.orange}`,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '9px 10px',
      }}>
        <div style={{ fontSize: '13px', flexShrink: 0, marginTop: '1px' }}>
          {TRIGGER_ICONS[snapshot.trigger_type] || '📸'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '12px',
            fontWeight: 600,
            color: T.ink,
            fontFamily: T.sans,
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {snapshot.trigger_description}
          </div>
          <div style={{
            fontSize: '11px',
            color: T.inkFaint,
            fontFamily: T.mono,
            marginTop: '2px',
          }}>
            Snapshot · v{snapshot.idea_spec_version} · {formatTime(snapshot.created_at)}
          </div>
        </div>
        <button
          onClick={onRollback}
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            padding: '2px 7px',
            fontSize: '11px',
            color: T.orange,
            backgroundColor: 'transparent',
            border: `1px dashed ${T.ruleStrong}`,
            borderRadius: T.full,
            cursor: 'pointer',
            fontFamily: T.sans,
          }}
          title="Rollback to this snapshot"
        >
          <ArrowUturnLeftIcon style={{ width: 10, height: 10 }} />
          Rollback
        </button>
      </div>
    </div>
  );
};

export default HistorySidebar;
